// THE GAME'S MUSIC: wires the pure mode logic (musicLogic.js) and the player (musicPlayer.js) to the game.
//   • the player's clicker presses ('paw:click' — not the citizens' automatic clicks) and every wild goon they slay drive the Relax ⇄ Juice switching
//   • nothing sounds until the first click or key press (browser rule), and only while the game page is showing in a visible tab
//   • volume, mute, Auto and a hand-pinned mode are remembered on this device
// The panel above the minimap (ui/musicPanel.js) reads state() and calls chooseMode / setAuto / setVolume / setMuted.

import { Events, on } from '../ui/eventBus.js'
import { createMusicLogic } from './musicLogic.js'
import { createMusicPlayer } from './musicPlayer.js'

export const TRACKS = Object.freeze({ relax: '/audio/soundtrack-zen.mp3', juice: '/audio/soundtrack-juice.mp3' })
const MODE_KEY = 'mao-kingdom/music-mode/v2' // (v2: choosing a mode by hand no longer switches Auto off — everyone starts with Auto on)

let logic = null, player = null
let warpMs = 0 // tests can move the clock forward (the 20-second "neglect" would otherwise take 20 real seconds)
const listeners = new Set()
let playPage = false
const clock = () => performance.now() + warpMs
const store = () => { try { return window.localStorage } catch { return null } }

const readMode = () => { try { return JSON.parse(store()?.getItem(MODE_KEY) || 'null') || {} } catch { return {} } }
const writeMode = (s) => { try { store()?.setItem(MODE_KEY, JSON.stringify({ auto: s.auto, mode: s.auto ? 'relax' : s.mode })) } catch { /* private mode */ } }

export function getMusic() {
  const l = logic.state(), p = player.state()
  return { mode: l.mode, auto: l.auto, volume: p.volume, muted: p.muted, muteLevel: p.muteLevel, started: p.started, active: p.active, needsGesture: p.needsGesture, tracks: p.tracks, recentClicks: l.recentClicks }
}
export function subscribeMusic(fn) { listeners.add(fn); return () => listeners.delete(fn) }
const notify = () => { const s = getMusic(); for (const fn of listeners) fn(s) }

export const chooseMode = (m) => { logic.choose(m, clock()); player.start(); notify() }
export const setAuto = (on) => { logic.setAuto(on, clock()); player.start(); notify() }
export const setVolume = (v) => { player.setVolume(v); player.start(); notify() }
export const setMuted = (m) => { player.setMuted(m); player.start(); notify() }
export const toggleMuted = () => setMuted(!getMusic().muted)
export const warp = (ms) => { warpMs += ms }
export const getElements = () => player.elements()

export function initMusic({ isPlayPage = () => true } = {}) {
  if (logic) return
  const saved = readMode()
  logic = createMusicLogic({
    auto: saved.auto !== false, mode: saved.auto === false && (saved.mode === 'juice' || saved.mode === 'relax') ? saved.mode : 'relax',
    onChange: ({ mode }) => { player?.setMode(mode); if (player) { writeMode(logic.state()); notify() } },
  })
  player = createMusicPlayer({ tracks: TRACKS, storage: store() })
  player.setMode(logic.state().mode)

  let lastActive = null
  const refreshActive = () => { playPage = isPlayPage(); const on = playPage && !document.hidden; player.setActive(on); if (on !== lastActive) { lastActive = on; notify() } }
  on(Events.PageChange, refreshActive)
  document.addEventListener('visibilitychange', refreshActive)
  const gesture = () => { const was = player.state().started; player.start(); refreshActive(); if (!was) notify() } // (a press anywhere only repaints the panel when something changed)
  window.addEventListener('pointerdown', gesture, { capture: true })
  window.addEventListener('keydown', gesture, { capture: true })

  on('paw:click', () => { logic.click(clock()); notify() }) // the player's own press on the clicker
  on(Events.WildGoonSlain, () => { logic.click(clock()); notify() }) // …and every goon they slay: fighting is as lively as clicking, so it brings in (and keeps) Juice

  let last = performance.now()
  setInterval(() => {
    const now = performance.now(), dt = Math.min(5000, now - last); last = now // (real elapsed time, so a busy page does not stretch a fade; capped so a long sleep does not skip one)
    const before = logic.state().mode
    logic.tick(clock()); player.step(dt)
    if (logic.state().mode !== before) notify()
  }, 50)
  refreshActive()
}
